// src/pages/auth/RegisterPage.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Eye, EyeOff, Mail, Lock, Loader, AlertCircle, User, ArrowRight } from 'lucide-react';

// רכיב מחוון חוזק סיסמה
const PasswordStrengthIndicator = ({ password }) => {
  const requirements = [
    {
      id: 'length',
      label: 'לפחות 8 תווים',
      test: (pwd) => pwd.length >= 8
    },
    {
      id: 'uppercase',
      label: 'אות גדולה באנגלית (A-Z)',
      test: (pwd) => /[A-Z]/.test(pwd)
    },
    {
      id: 'lowercase',
      label: 'אות קטנה באנגלית (a-z)',
      test: (pwd) => /[a-z]/.test(pwd)
    },
    {
      id: 'number',
      label: 'מספר אחד לפחות (0-9)',
      test: (pwd) => /[0-9]/.test(pwd)
    },
    {
      id: 'special',
      label: 'תו מיוחד (!@#$%^&*)',
      test: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)
    }
  ];

  const passedRequirements = requirements.filter(req => req.test(password));
  const strengthPercentage = (passedRequirements.length / requirements.length) * 100;

  const getStrengthColor = () => {
    if (strengthPercentage < 40) return 'bg-red-500';
    if (strengthPercentage < 60) return 'bg-orange-500';
    if (strengthPercentage < 80) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStrengthText = () => {
    if (strengthPercentage < 40) return 'חלשה';
    if (strengthPercentage < 60) return 'בינונית';
    if (strengthPercentage < 80) return 'חזקה';
    return 'מצוינת';
  };

  return (
    <div className="mt-2 space-y-2">
      {/* פס חוזק סיסמה */}
      <div className="space-y-1">
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-600">חוזק סיסמה:</span>
          <span className={`text-xs font-medium ${
            strengthPercentage < 40 ? 'text-red-600' :
            strengthPercentage < 60 ? 'text-orange-600' :
            strengthPercentage < 80 ? 'text-yellow-600' : 'text-green-600'
          }`}>
            {getStrengthText()}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor()}`}
            style={{ width: `${strengthPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* רשימת דרישות */}
      <div className="space-y-1.5">
        {requirements.map((requirement) => {
          const isPassed = requirement.test(password);
          return (
            <div key={requirement.id} className="flex items-center gap-2">
              <div className={`w-4 h-4 rounded-full flex items-center justify-center transition-all duration-200 ${
                isPassed 
                  ? 'bg-green-100 border-2 border-green-500' 
                  : 'bg-gray-100 border-2 border-gray-300'
              }`}>
                {isPassed && (
                  <svg className="w-2.5 h-2.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"></path>
                  </svg>
                )}
              </div>
              <span className={`text-xs transition-colors duration-200 ${
                isPassed ? 'text-green-700 font-medium' : 'text-gray-600'
              }`}>
                {requirement.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // פונקציה לבדיקת חוזק סיסמה
  const isPasswordStrong = (password) => {
    const requirements = [
      password.length >= 8,
      /[A-Z]/.test(password),
      /[a-z]/.test(password),
      /[0-9]/.test(password),
      /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    ];
    return requirements.every(req => req);
  };

  const validateStep1 = () => {
    if (!formData.firstName.trim()) {
      setError('נדרש שם פרטי');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('נדרש שם משפחה');
      return false;
    }
       // 🆕 הוספת בדיקה לטלפון
    if (!formData.phone.trim()) {
      setError('נדרש מספר טלפון');
      return false;
    }
    // 🆕 בדיקת פורמט טלפון בסיסית
    const phoneRegex = /^05\d{8}$/;
    if (!phoneRegex.test(formData.phone.replace(/[-\s]/g, ''))) {
      setError('מספר טלפון לא תקין (צריך להתחיל ב-05 ו-10 ספרות)');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    // בדיקת אימייל
    if (!formData.email.trim()) {
      setError('נדרשת כתובת אימייל');
      return false;
    }
    
    // בדיקת אישור אימייל
    if (!formData.confirmEmail.trim()) {
      setError('נדרש אישור כתובת אימייל');
      return false;
    }
    
    // בדיקה שהאימיילים זהים
    if (formData.email !== formData.confirmEmail) {
      setError('כתובות האימייל אינן זהות');
      return false;
    }
    
    // בדיקת פורמט אימייל
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('כתובת אימייל לא תקינה');
      return false;
    }
    
    // 🆕 בדיקת חוזק סיסמה
    if (!isPasswordStrong(formData.password)) {
      setError('הסיסמה חייבת לעמוד בכל הדרישות המוצגות למטה');
      return false;
    }
    
    // בדיקת אישור סיסמה
    if (formData.password !== formData.confirmPassword) {
      setError('הסיסמאות אינן זהות');
      return false;
    }
    
    return true;
  };

  const handleNextStep = () => {
    setError('');
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateStep2()) return;

    setLoading(true);

    const registrationData = {
      ...formData,
      pw: formData.password
    };

    const result = await register(registrationData);
    
    if (result.success) {
      navigate('/', { replace: true });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        
        {/* חזרה לדף הבית */}
        <Link 
          to="/" 
          className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-6 group"
        >
          <ArrowRight className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">חזרה לדף הבית</span>
        </Link>

        {/* כרטיס הרשמה */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          
          {/* כותרת */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-8 py-6 text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">הרשמה</h1>
            <p className="text-purple-100">הצטרפו לגמלנט AI</p>
            
            {/* מחוון שלבים */}
            <div className="flex justify-center mt-4 gap-2">
              <div className={`w-3 h-3 rounded-full ${step >= 1 ? 'bg-white' : 'bg-white/30'}`}></div>
              <div className={`w-3 h-3 rounded-full ${step >= 2 ? 'bg-white' : 'bg-white/30'}`}></div>
            </div>
          </div>

          {/* תוכן הטופס */}
          <div className="p-8">
            
            {/* הודעת שגיאה */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            )}

            {step === 1 ? (
              /* שלב 1: פרטים אישיים */
              <form onSubmit={(e) => { e.preventDefault(); handleNextStep(); }} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    שם פרטי
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all duration-300"
                    placeholder="הזן שם פרטי"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    שם משפחה
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all duration-300"
                    placeholder="הזן שם משפחה"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    טלפון 
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all duration-300"
                    placeholder="05X-XXXXXXX"
                    dir="ltr"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3 px-4 rounded-xl text-lg font-bold bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
                >
                  המשך
                </button>
              </form>
            ) : (
              /* שלב 2: אימייל וסיסמה */
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* חזרה לשלב הקודם */}
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center gap-1"
                >
                  ← חזור לשלב הקודם
                </button>

                {/* כתובת אימייל ראשונה */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    כתובת אימייל
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full pl-4 pr-10 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all duration-300"
                      placeholder="example@gmail.com"
                      dir="ltr"
                    />
                  </div>
                </div>

                {/* אישור כתובת אימייל */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    אישור כתובת אימייל
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      name="confirmEmail"
                      required
                      value={formData.confirmEmail}
                      onChange={handleInputChange}
                      className={`w-full pl-4 pr-10 py-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none transition-all duration-300 ${
                        formData.confirmEmail && formData.email !== formData.confirmEmail 
                          ? 'border-red-300 focus:border-red-500' 
                          : formData.confirmEmail && formData.email === formData.confirmEmail
                            ? 'border-green-300 focus:border-green-500'
                            : 'border-gray-200 focus:border-purple-500'
                      }`}
                      placeholder="הזן שוב את כתובת האימייל"
                      dir="ltr"
                    />
                    {/* אינדיקטור התאמה */}
                    {formData.confirmEmail && (
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                        {formData.email === formData.confirmEmail ? (
                          <span className="text-green-500 text-xs">✓</span>
                        ) : (
                          <span className="text-red-500 text-xs">✗</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* סיסמה עם מחוון חוזק */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    סיסמה
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      required
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full pl-12 pr-10 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all duration-300"
                      placeholder="הזן סיסמה חזקה"
                      dir="ltr"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 left-0 pl-3 flex items-center hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  
                  {/* 🆕 מחוון חוזק סיסמה */}
                  {formData.password && (
                    <PasswordStrengthIndicator password={formData.password} />
                  )}
                </div>

                {/* אישור סיסמה */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    אישור סיסמה
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      required
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={`w-full pl-12 pr-10 py-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none transition-all duration-300 ${
                        formData.confirmPassword && formData.password !== formData.confirmPassword 
                          ? 'border-red-300 focus:border-red-500' 
                          : formData.confirmPassword && formData.password === formData.confirmPassword && isPasswordStrong(formData.password)
                            ? 'border-green-300 focus:border-green-500'
                            : 'border-gray-200 focus:border-purple-500'
                      }`}
                      placeholder="הזן שוב את הסיסמה"
                      dir="ltr"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 left-0 pl-3 flex items-center hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !isPasswordStrong(formData.password)}
                  className={`
                    w-full py-3 px-4 rounded-xl text-lg font-bold
                    flex items-center justify-center gap-3
                    ${loading || !isPasswordStrong(formData.password)
                      ? 'bg-gray-300 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700'
                    }
                    text-white transition-all duration-300
                    transform hover:scale-105 active:scale-95
                    shadow-lg hover:shadow-xl
                  `}
                >
                  {loading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>נרשם...</span>
                    </>
                  ) : (
                    <>
                      <User className="w-5 h-5" />
                      <span>הירשם</span>
                    </>
                  )}
                </button>
              </form>
            )}

            {/* קישור להתחברות */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-gray-600 mb-4">כבר יש לך חשבון?</p>
              <Link
                to="/auth/login"
                className="inline-flex items-center px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-medium transition-all duration-300 transform hover:scale-105"
              >
                <span>התחבר</span>
                <ArrowRight className="w-4 h-4 mr-2" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;